// Ionic & Angular Imports
import {Component} from '@angular/core';
import {Events, NavParams, LoadingController, Loading, ModalController, AlertController, Refresher} from 'ionic-angular';

// Service Imports 
import {rsReportSvc, ReportState } from '../../../../services/report.service';
import {rsActivitySvc, IActivity} from '../../../../services/activity.service';

// Component Imports
import {BasePage} from '../../../base.component';
import {ActivityModal} from './activity-modal/activity-modal.component';

@Component({
    templateUrl: 'activity.html',
    providers: [
        rsActivitySvc
    ]
})
export class ActivityPage extends BasePage
{
    // Class variables 
    private contentList : IActivity[];

    // Class Constructor
    constructor( 
        protected alertCtrl : AlertController, 
        private loadingCtrl : LoadingController,  
        private navParams : NavParams, 
        private eventSystem : Events,
        private modalCtrl : ModalController,
        private reportSvc: rsReportSvc, 
        private activitySvc : rsActivitySvc,
    ){
        // Construct base class
        super(alertCtrl);

        // Retrieve the activity list from our NavParams       
        this.contentList = this.navParams.get('content');
        this.sortContentList();

        // Register our event handlers 
        this.registerEventHandlers( eventSystem );
    }

    // Class method used to subscribe to all events this class is interested in
    private registerEventHandlers( eventSystem : Events ) {
        eventSystem.subscribe( 'activity:deleted', (data) => { this.deleteActivity( data ); });
        eventSystem.subscribe( 'activity:updated', (data) => { this.updateActivity( data ); });
        eventSystem.subscribe( 'activity:added',   (data) => { this.addActivity( data ); });
    }
        
    // Sorting method for ensuring our ContentList is always sorted by id (ie., oldest to newest entries)
    private sortContentList(reverse : boolean = false) {
        this.contentList.sort( (a,b) => {
            return reverse ? b.id - a.id : a.id - b.id;
        });
    }

    // Class method used to add a new activity to our ContentList 
    private addActivity( activity : IActivity ) : boolean {
        // Add this activity to the end of our ContentList  
        let length : number = this.contentList.push(activity);

        // Finally, publish an event that our report has been updated
        this.eventSystem.publish( 'report:updated', ReportState.Activity, length );      
        
        // Return successful
        return true;  
    }

    // Class method used to update an activity in our ContentList
    private updateActivity( activity : IActivity ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching activity
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : IActivity = this.contentList[idx];
            if( item.id === activity.id ) {
                // Update the content of this activity within the ContentList
                this.contentList[idx] = activity;
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used to delete an activity in our ContentList
    private deleteActivity( activity : IActivity ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching activity
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : IActivity = this.contentList[idx];
            if( item.id === activity.id ) {
                // Remove this activity from the ContentList
                this.contentList.splice( idx, 1 );

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Activity, this.contentList.length );

                // Return successful
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used by the template to edit an existing activity
    public updateContent( activity : IActivity ) {
        // Create our ModalInstance and configure for editing
        this.modalCtrl.create( ActivityModal, { 
            activity: activity,
            editmode: true
        }).present();
    }

    // Class method used by the template to create a new activity
    public createContent( ) {
          // Create our ModalInstance and configure for creating 
        this.modalCtrl.create( ActivityModal, { 
            activity: null,
            editmode: false
        }).present();      
    }

    // Class method used by the template to delete an activity
    public deleteContent( activity : IActivity ) {
        // Verify that we have a valid activity id before trying to delete 
        if( activity.id > 0 ) 
        {
            // Activity id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Activity', 'Are you sure you want to delete "' + activity.title + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.activitySvc.deleteActivity( activity.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('activity:deleted', activity );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this activity.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        loader.dismiss();
                    });
                });
            });
        } else {
            // The activity id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }

    // Class method used by the template to refresh the ContentList
    public refreshContent( refresher : Refresher ) {
        // Spawn a spinner while the ContentList is being refreshed
        let loader : Loading = this.loadingCtrl.create( {
            content: 'Refreshing...'
        });
        loader.present().then( () => {
            // The spinner is loaded, so let's get our refreshed ContentList
            this.activitySvc.getActivities( this.reportSvc.getActiveReportId() )
            .subscribe( result => {
                // Data was received from our service 
                this.contentList = result;
                this.sortContentList();

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Activity, this.contentList.length );
            }, err => {
                // The operation failed, so let's handle this error 
                loader.dismiss().then( () => {
                    this.displayError( "There was an error refreshing the list.  Please try again later." ).then( () => {
                        refresher.complete();
                    });
                });
            }, () => {
                // The operation succeeded, let's dismiss our spinner and complete our refresh operation 
                loader.dismiss().then( () => {
                    refresher.complete();
                });
            });
        });
    }
}



// WEBPACK FOOTER //
// ./src/pages/report/tab-main/activity/activity.component.ts